import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';

import { ChecklistItem, PDFImageProps, User, AnomalyLocationData, FVIStructureItem, IncludeSummary, PIDData, CustomUTMOverview, UTMInspection } from '../../../utils/models';
import { getTutorials } from '../../../store/tutorials';
import { getCoverData, initialFVICoverData } from '../../../store/coverData';
import { matchPhrases, reArrangeInspectionItemAnomalies, reArrangeGeneralPhotos, validateWorkOrderNumber, reArrangePhotoRecord } from '../../../utils/utilities';
import { DEFAULT_REPORT_TITLE,  TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';
import { getCurrentUser } from '../../../services/authService';
import { getReportDetails, setItemsPerPage, setLastPhotoNumber } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Button from '../../themed/button';
import CustomCoverForm from '../../forms/custom-fvi/coverForm';
import Conditional from '../../common/conditional';
import Elevation from '../../themed/instruction-modals/elevation';
import ExecutiveSummaryForm from '../../forms/visual-inspection/executiveSummaryForm';
import FVICoverData from '../../themed/fvi-cover-data';
import LoadingBadge from '../../themed/loading-badge';
import Modal from '../../themed/modal';
import Switch from '../../themed/switch';
import FviTankReport from './fviTankReport';
import ReportTitleForm from '../../forms/custom-fvi/reportTitleForm';
import StructureItems from './structure-items';
import SyncTime from '../../blocks/sync-time';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useIncompleteInspectedSections from '../../../hooks/useIncompleteInspectedSections';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CreateCustomFVITankReport: React.FC = () => {
	const { activeTutorial } = useAppSelector(getTutorials);
	const { projectDetails, error } = useProjectDetails();
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [additionalComment, setAdditionalComment] = useState('');
	const [anomalyLocationData, setAnomalyLocationData] = useState<AnomalyLocationData>({ hasAnomalyLocations: 'yes', anomalyLocations: [] });
	const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>(initialChecklistItems);
	const [coverData, setCoverData] = useState(initialFVICoverData);
	const [executiveSummary, setExecutiveSummary] = useState('');
	const [includeSummary, setIncludeSummary] = useState<IncludeSummary>('include');
	const [reportTitle, setReportTitle] = useState(DEFAULT_REPORT_TITLE);
	const [generalPhotos, setGeneralPhotos] = useState<PDFImageProps[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [pidData, setPIDData] = useState<PIDData>({ hasPID: 'yes', pIDNumber: '', pIDImages: [] });
	const [structureItems, setStructureItems] = useState<FVIStructureItem[]>([]);
	const [tmlLocations, setTMLLocations] = useState<PDFImageProps[]>([]);
	const [utInspectionOverview, setUTInspectionOverview] = useState(initialUTMOverview);
	const [utInspectionItems, setUTInspectionItems] = useState<UTMInspection[]>([]);

	const { role } = getCurrentUser() as User;
	const { incompleteSections, isLastInspectedItemComplete } = useIncompleteInspectedSections(structureItems);

	const reportDetails = useMemo(() => {
		return {
			anomalyLocationData,
			additionalComment,
			checklistItems,
			coverData,
			executiveSummary,
			generalPhotos,
			pidData,
			includeSummary,
			projectDetails,
			reportTitle,
			structureItems,
			utInspectionItems,
			utInspectionOverview,
			tmlLocations
		};
	}, [projectDetails, additionalComment, anomalyLocationData, checklistItems, coverData, executiveSummary, generalPhotos, includeSummary, pidData, structureItems, utInspectionItems, utInspectionOverview, tmlLocations, reportTitle]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const handleToggleSummary = useCallback(() => {
		setIncludeSummary((previous) => previous === 'include' ? 'exclude' : 'include');
	}, []);

	const validationMessage = useMemo(() => {
		if (!validateWorkOrderNumber(reportDetails.coverData.workOrderNumber))
			return 'Enter a valid work order number!';

		if (matchPhrases(reportDetails.anomalyLocationData.hasAnomalyLocations, 'yes') && reportDetails.anomalyLocationData.anomalyLocations.length === 0) {
			return 'Please Upload Anomaly Location images!';
		}

		if (!isLastInspectedItemComplete) {
			return `Please complete sections ${incompleteSections.join(', ')}`;
		}

		return '';
	}, [isLastInspectedItemComplete, incompleteSections, reportDetails]);

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	useInspectedItemIDUpdate(structureItems.map((item) => item.structureMemberName));

	useEffect(() => {
		function initializeReport() {
			const cachedReport = autoSave.oldReportData as typeof reportDetails | null;
			if (!cachedReport) {
				if (fviCoverData.coverData.length > 0) setCoverData(fviCoverData);
				
				return;
			}

			const rearrangedAnomalies = reArrangeInspectionItemAnomalies(cachedReport.structureItems) as any[];
			const rearrangedGeneralPhotos = reArrangeGeneralPhotos(rearrangedAnomalies) as any[];

			if (cachedReport.utInspectionItems) {
				setUTInspectionItems(cachedReport.utInspectionItems);
			}

			if (cachedReport.utInspectionOverview) {
				setUTInspectionOverview(cachedReport.utInspectionOverview);
			}

			if (cachedReport.tmlLocations) {
				setTMLLocations(cachedReport.tmlLocations);
			}

			const photoRecord = reArrangePhotoRecord(cachedReport.generalPhotos);
			
			setGeneralPhotos(photoRecord);
			setAdditionalComment(cachedReport.additionalComment);
			setAnomalyLocationData(cachedReport.anomalyLocationData);
			setCoverData(cachedReport.coverData?.coverData?.length > 0 ? cachedReport.coverData : fviCoverData);
			setExecutiveSummary(cachedReport.executiveSummary);
			setChecklistItems(cachedReport.checklistItems)
			setIncludeSummary(cachedReport.includeSummary);
			setPIDData(cachedReport.pidData);
			setReportTitle(cachedReport.reportTitle);
			setStructureItems(rearrangedGeneralPhotos);

			dispatch(setLastPhotoNumber(photoRecord.length));
		}

		initializeReport();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.EFVIS));
	}, [dispatch]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<FviTankReport {...reportDetails} />
			</Modal>

			<div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			{projectDetails && (
				<FVICoverData
					coverDataId={config.coverDataTemplates.exxonMobilFVITank[projectDetails.organisationId]}
					isVisible={projectDetails.inspectionTypeId === config.supportedTemplates.exxonMobilFVITank && role.approveReports}
					subtitle={`Update the Cover Data information. This is the default data used on all ${projectDetails.organisationName} FVI Tank Reports`}
				/>
			)}

			<Zippy headerTitle="Report Title" isValid={reportTitle.length > 0}>
				<ReportTitleForm
					projectTitle={projectDetails?.projectTitle}
					onAdd={setReportTitle}
				/>
			</Zippy>

			<Zippy headerTitle="Cover Sheet" isValid={coverData.coverData.length > 0}>
				<CustomCoverForm
					initialData={coverData}
					onAddCoverForm={setCoverData}
				/>
			</Zippy>

			<Zippy headerTitle="Executive Summary" isValid={executiveSummary.length > 0}>
				<div className="d-flex justify-content-end mt-2">
					<Switch 
						checked={includeSummary === 'include'}
						label='Include Inspection Breakdown'
						onChange={handleToggleSummary} 
					/>
				</div>
				
				<div className="my-4 divider"></div>

				<ExecutiveSummaryForm
					onProceed={setExecutiveSummary}
					initialSummary={executiveSummary}
				/>
			</Zippy>

			<StructureItems
				hasPDO='no'
				onChangeHasPDO={() => {}}
				pdoChecklistItems={[]}
				onChangePDOChecklistItems={() => {}}
				additionalComment={additionalComment}
				checklistLabel="TANK"
				checklistItems={checklistItems} 
				onChangeAdditionalComment={setAdditionalComment}
				onChangeChecklistItems={setChecklistItems}
				onChangeAnomalyLocationData={setAnomalyLocationData}
				anomalyLocationData={anomalyLocationData}
				generalPhotos={generalPhotos}
				onChangeGeneralPhotos={setGeneralPhotos}
				pidData={pidData}
				onChangePIDData={setPIDData}
				items={structureItems} 
				onItemsChange={setStructureItems} 
			/>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const initialChecklistItems: ChecklistItem[] = [
	{
		id: '1',
		lineNumber: 'Shell Coating Condition',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'shell',
	},
	{
		id: '2',
		lineNumber: 'Shell for distortion',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'shell',
	},
	{
		id: '3',
		lineNumber: 'Shell plate-to-shell plate welds',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'shell',
	},
	{
		id: '4',
		lineNumber: "Shell plate-to-bottom plate weld",
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'shell',
	},
	{
		id: '5',
		lineNumber: 'Shell nozzles and manways including attachment welds',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'shell',
	},
	{
		id: '6',
		lineNumber: 'Internal lining or coating',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '7',
		lineNumber: 'Inspect the annular-to-plate welds and plate-to-plate welds',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '8',
		lineNumber: 'Bottom plates',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '9',
		lineNumber: 'Piping (associated with floor)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '10',
		lineNumber: 'Support/reinforcement plates (for legs and anode brackets)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '11',
		lineNumber: 'Floating roof leg wear plates',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '12',
		lineNumber: 'Bottom settlement',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '13',
		lineNumber: 'Cathodic protection (CP) system',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '14',
		lineNumber: 'Water draw-off sump',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '15',
		lineNumber: 'Associated pipe',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'bottom floor',
	},
	{
		id: '16',
		lineNumber: 'Plates',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '17',
		lineNumber: 'Floating roof seals',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '18',
		lineNumber: 'Anti-rotation pole and attachment bracelet',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '19',
		lineNumber: 'Circumferential deck stiffeners',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '20',
		lineNumber: 'Guage well/float mechanism',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '21',
		lineNumber: 'Roof support legs and sleeves with attachment welds',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '22',
		lineNumber: 'Bearing pads (underneath supports)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '23',
		lineNumber: 'Roof drain piping and swivel joints',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '24',
		lineNumber: 'Water draw-off',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '25',
		lineNumber: 'Pontoon compartment',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'floating roof',
	},
	{
		id: '26',
		lineNumber: 'Plates (underside)',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'fixed roof',
	},
	{
		id: '27',
		lineNumber: 'Roof beams and rafters',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'fixed roof',
	},
	{
		id: '28',
		lineNumber: 'Support columns',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'fixed roof',
	},
	{
		id: '29',
		lineNumber: 'Plate welds',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'fixed roof',
	},
	{
		id: '30',
		lineNumber: 'Frangible weld',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'fixed roof',
	},
	{
		id: '31',
		lineNumber: 'Pressure / vaccum vents',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'fixed roof',
	},
	{
		id: '32',
		lineNumber: 'Level floats, if applicable',
		assessment: '',
		anomalyItems: [],
		error: true,
		comment: '',
		category: 'fixed roof',
	},
];

const initialUTMOverview: Omit<CustomUTMOverview, 'data'> = {
	ndeTechnique: '',
	equipment: '',
	probe: '',
	amplitudeLinearityCheck: '',
	screenHeightLinearityCheck: '',
	calibrationBlk: '',
	refStd: '',
	serialNo: '',
	scanningLevel: '',
	couplant: '',
	remarks: '',
	material: '',
	calibrationDueDate: new Date(),
	dateOfUTM: null,
	corrosionAllowance: 0,
	unit: 'mm',
	summary: '',
	techniqueType: '',
	velocity: 0,
};

export default CreateCustomFVITankReport;
